import { responseError } from '@/utils/errors';
import findResult, { assignIfExists, mapResults, jsonTryParse } from '@/utils/resultsHelper';
import { addUUIDsToArray, findWithUUID, removeItemByUUID } from '@/utils/uuidHelper';
import { states, updateStates } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import actions, { vocabularyGroupings, vocabularyLifeCycle } from '../constants';
import initialState, {
  activeVocabulary,
  newVocabularyLink,
  newVocabularyProperties,
  pruneVocabularyErrors,
  vocabulariesResult,
  emailTemplate,
  newVocabularyRoleAction,
  findVocabulary,
  findAllVocabularies, findVocabularies,
} from './state';

const addVocabularyLink = withDraft((draft) => {
  draft.activeVocabulary.urls.push(newVocabularyLink());
  draft.activeVocabulary.updateErrors = [];
});

const addVocabularyRoleAction = withDraft((draft) => {
  draft.activeVocabulary.roleActions.push(newVocabularyRoleAction());
  draft.activeVocabulary.updateErrors = [];
});

const applyVocabularies = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);
  item.results = mapResults([], action, vocabulariesResult);
});

const closeVocabularies = withDraft((draft, { category }) => {
  draft.vocabularies[category] = initialState.vocabularies[category];
});

const deleteVocabularyLink = withDraft((draft, action) => {
  draft.activeVocabulary.urls = removeItemByUUID(draft.activeVocabulary.urls, action.linkUUID);
});

const deleteVocabularyRoleAction = withDraft((draft, action) => {
  draft.activeVocabulary.roleActions = removeItemByUUID(draft.activeVocabulary.roleActions, action.uuid);
});

const editVocabulary = withDraft((draft, action) => {
  const categories = findAllVocabularies(draft, action);
  const { grouping, value } = action.result;
  const props = { ...updateStates };

  if ([vocabularyGroupings.URLS, vocabularyGroupings.CUSTOM_LINKS].includes(grouping)) {
    props.urls = addUUIDsToArray(jsonTryParse(value, []));
  } else if ([vocabularyGroupings.ROLE_ACTIONS].includes(grouping)) {
    props.roleActions = addUUIDsToArray(jsonTryParse(value, []));
  } else if ([vocabularyGroupings.EMAIL_TEMPLATE].includes(grouping)) {
    props.email = jsonTryParse(value, emailTemplate);
  }

  draft.activeVocabulary = activeVocabulary();
  categories.forEach((category) => {
    const item = findResult(draft.vocabularies, category, action.code);
    assignIfExists(item, { ...action.result, ...props });
  });
});

const editVocabularyError = withDraft((draft, action) => {
  assignIfExists(draft.activeVocabulary, {
    updateStatus: states.ERROR,
    updateErrors: [responseError(action)],
  });
});

const fetchVocabularies = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const hideSaveVocabulary = withDraft((draft) => { draft.activeVocabulary = activeVocabulary(); });

const resetVocabulary = withDraft((draft, action) => {
  const item = findVocabulary(draft, action);
  item.updateStatus = states.PENDING;
});

const resetVocabularyError = withDraft((draft, action) => {
  const item = findVocabulary(draft, action);
  item.updateStatus = states.NONE;
});

const resetVocabularySuccess = withDraft((draft, action) => {
  const item = findVocabulary(draft, action);
  assignIfExists(item, {
    ...vocabulariesResult(action.result),
    ...updateStates,
  });
});

const showSaveVocabulary = withDraft((draft, action) => {
  const item = findVocabulary(draft, action);
  assignIfExists(draft.activeVocabulary, newVocabularyProperties(item, action));
});

const showVocabularies = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, vocabulariesResult) : mapResults([], action, vocabulariesResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showVocabulariesError = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const updateActiveVocabularyProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeVocabulary, action.properties);
  draft.activeVocabulary.updateErrors = pruneVocabularyErrors(draft.activeVocabulary.updateErrors, action);
});

const updateVocabulariesFilter = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);
  item.filter = action.value;
});

const updateVocabulary = withDraft((draft) => {
  draft.activeVocabulary.updateStatus = states.PENDING;
});

const updateVocabularyEmailProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeVocabulary.email, action.properties);
  draft.activeVocabulary.updateErrors = [];
});

const updateVocabularyLinkProperties = withDraft((draft, action) => {
  const item = findWithUUID(draft.activeVocabulary.urls, action.linkUUID);
  assignIfExists(item, action.properties);
  draft.activeVocabulary.updateErrors = [];
});

const updateVocabularyProperties = withDraft((draft, action) => {
  const item = findVocabulary(draft, action);
  assignIfExists(item, action.properties, vocabularyLifeCycle, true);
});

const updateVocabularyRoleActionProperties = withDraft((draft, action) => {
  const item = findWithUUID(draft.activeVocabulary.roleActions, action.uuid);
  assignIfExists(item, action.properties);
  draft.activeVocabulary.updateErrors = [];
});

const toggleVocabulariesPresentation = withDraft((draft, action) => {
  const item = findVocabularies(draft, action);

  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

export default {
  [actions.ADD_VOCABULARY_LINK]: addVocabularyLink,
  [actions.ADD_VOCABULARY_ROLE_ACTION]: addVocabularyRoleAction,
  [actions.APPLY_VOCABULARIES]: applyVocabularies,
  [actions.CLOSE_VOCABULARIES]: closeVocabularies,
  [actions.DELETE_VOCABULARY_LINK]: deleteVocabularyLink,
  [actions.DELETE_VOCABULARY_ROLE_ACTION]: deleteVocabularyRoleAction,
  [actions.EDIT_VOCABULARY]: editVocabulary,
  [actions.EDIT_VOCABULARY_ERROR]: editVocabularyError,
  [actions.FETCH_VOCABULARIES]: fetchVocabularies,
  [actions.HIDE_SAVE_VOCABULARY]: hideSaveVocabulary,
  [actions.REFRESH_VOCABULARIES]: fetchVocabularies,
  [actions.RESET_VOCABULARY]: resetVocabulary,
  [actions.RESET_VOCABULARY_ERROR]: resetVocabularyError,
  [actions.RESET_VOCABULARY_SUCCESS]: resetVocabularySuccess,
  [actions.SHOW_SAVE_VOCABULARY]: showSaveVocabulary,
  [actions.SHOW_VOCABULARIES]: showVocabularies,
  [actions.SHOW_VOCABULARIES_ERROR]: showVocabulariesError,
  [actions.TOGGLE_VOCABULARIES_PRESENTATION]: toggleVocabulariesPresentation,
  [actions.UPDATE_ACTIVE_VOCABULARY_PROPERTIES]: updateActiveVocabularyProperties,
  [actions.UPDATE_VOCABULARIES_FILTER]: updateVocabulariesFilter,
  [actions.UPDATE_VOCABULARY]: updateVocabulary,
  [actions.UPDATE_VOCABULARY_EMAIL_PROPERTIES]: updateVocabularyEmailProperties,
  [actions.UPDATE_VOCABULARY_LINK_PROPERTIES]: updateVocabularyLinkProperties,
  [actions.UPDATE_VOCABULARY_PROPERTIES]: updateVocabularyProperties,
  [actions.UPDATE_VOCABULARY_ROLE_ACTION_PROPERTIES]: updateVocabularyRoleActionProperties,
};
